import { useState } from 'react'
import { ArrowDownRight, ArrowUpRight, GitCompareArrows, Loader2, Minus, X } from 'lucide-react'
import clsx from 'clsx'
import type { CompareResult, Totals } from './types'
import { quarters, type QuarterSpec } from './periods'
import { count, moneyExact, pct, ratio } from './format'

const METRICS: Array<{ key: keyof Totals; label: string; render: (t: Totals) => string }> = [
  { key: 'dealCount', label: 'Deals', render: (t) => count(t.dealCount) },
  { key: 'totalVolume', label: 'Volume', render: (t) => moneyExact(t.totalVolume) },
  { key: 'avgDealSize', label: 'Avg size', render: (t) => moneyExact(t.avgDealSize) },
  { key: 'closedCount', label: 'Closed', render: (t) => count(t.closedCount) },
  { key: 'closedVolume', label: 'Closed vol.', render: (t) => moneyExact(t.closedVolume) },
  { key: 'avgLtv', label: 'Avg LTV', render: (t) => pct(t.avgLtv) },
  { key: 'avgDscr', label: 'Avg DSCR', render: (t) => ratio(t.avgDscr) },
]

function DeltaCell({ delta }: { delta?: { absolute: number | null; percent: number | null } }) {
  if (!delta || delta.absolute === null) {
    return <span className="text-[10px] text-stone-600">—</span>
  }
  const flat = delta.absolute === 0
  const up = delta.absolute > 0
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 font-mono text-[10px]',
        flat ? 'text-stone-500' : up ? 'text-emerald-300' : 'text-red-300',
      )}
    >
      <Icon size={10} />
      {delta.percent === null ? 'n/a' : `${delta.percent > 0 ? '+' : ''}${delta.percent.toFixed(1)}%`}
    </span>
  )
}

export function ComparePanel({
  dateField,
  result,
  loading,
  error,
  onRun,
  onClose,
}: {
  dateField: string
  result: CompareResult | null
  loading: boolean
  error: string | null
  onRun: (args: { dateField: string; periods: QuarterSpec[] }) => void
  onClose: () => void
}) {
  const options = quarters(new Date())
  const [field, setField] = useState(dateField === 'created_at' ? 'created_at' : 'funded_at')
  const [left, setLeft] = useState(options[1]?.label ?? options[0]?.label ?? '')
  const [right, setRight] = useState(options[0]?.label ?? '')

  const run = () => {
    const a = options.find((q) => q.label === left)
    const b = options.find((q) => q.label === right)
    if (!a || !b) return
    onRun({ dateField: field, periods: [a, b] })
  }

  return (
    <section
      aria-label="Period comparison"
      className="flex-shrink-0 rounded-lg border border-amber-300/15 bg-[#0b0b0b]"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-amber-300/10 px-3 py-2">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
          <GitCompareArrows size={11} /> Period comparison
        </span>

        <select
          value={field}
          onChange={(event) => setField(event.target.value)}
          aria-label="Comparison date basis"
          className="rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
        >
          <option value="funded_at">by close date</option>
          <option value="created_at">by entered date</option>
        </select>

        <select
          value={left}
          onChange={(event) => setLeft(event.target.value)}
          aria-label="Baseline period"
          className="rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
        >
          {options.map((q) => (
            <option key={q.label} value={q.label}>
              {q.label}
            </option>
          ))}
        </select>
        <span className="text-[10px] text-stone-600">vs</span>
        <select
          value={right}
          onChange={(event) => setRight(event.target.value)}
          aria-label="Comparison period"
          className="rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
        >
          {options.map((q) => (
            <option key={q.label} value={q.label}>
              {q.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={run}
          disabled={loading || !left || !right}
          className="flex items-center gap-1 rounded border border-amber-300/25 bg-amber-400/[0.1] px-2 py-1 text-[10px] text-amber-100 transition hover:bg-amber-400/[0.16] disabled:opacity-40"
        >
          {loading && <Loader2 size={10} className="animate-spin" />}
          Compare
        </button>

        <span className="ml-auto text-[9px] text-stone-600">
          Current filters apply to both periods
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close period comparison"
          className="rounded p-1 text-stone-500 transition hover:text-amber-200"
        >
          <X size={12} />
        </button>
      </div>

      {error ? (
        <p className="px-3 py-3 text-[11px] text-red-300">{error}</p>
      ) : !result ? (
        <p className="px-3 py-3 text-[11px] text-stone-600">
          Pick two periods and compare. Every non-date filter stays fixed, so the only difference
          between the columns is the window.
        </p>
      ) : (
        <div className="overflow-x-auto px-3 py-2">
          <table className="w-full min-w-[520px] text-left">
            <thead>
              <tr className="text-[9px] uppercase tracking-[0.14em] text-stone-500">
                <th scope="col" className="py-1 pr-3 font-medium">
                  Metric
                </th>
                {result.periods.map((period) => (
                  <th key={period.label} scope="col" className="py-1 pr-3 text-right font-medium">
                    {period.label}
                    <div className="font-mono text-[8px] normal-case tracking-normal text-stone-600">
                      {period.window?.from ?? '—'} → {period.window?.to ?? '—'}
                    </div>
                  </th>
                ))}
                <th scope="col" className="py-1 text-right font-medium">
                  Change
                </th>
              </tr>
            </thead>
            <tbody>
              {METRICS.map((metric) => (
                <tr key={String(metric.key)} className="border-t border-white/[0.04]">
                  <th scope="row" className="py-1.5 pr-3 text-[11px] font-normal text-stone-400">
                    {metric.label}
                  </th>
                  {result.periods.map((period) => (
                    <td
                      key={period.label}
                      className="py-1.5 pr-3 text-right font-mono text-[11px] text-stone-100"
                    >
                      {metric.render(period.totals)}
                    </td>
                  ))}
                  <td className="py-1.5 text-right">
                    <DeltaCell
                      delta={result.periods[result.periods.length - 1]?.delta?.[String(metric.key)]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
